import { onTypeReorder } from '@griply/utils';
import { useUpdateTasks } from 'features/tasks';
import React, { useMemo } from 'react';
import {
  rootId,
  useTree,
  VirtualizedTreeList,
} from 'shared/components/ui/virtualized-tree-list';
import {
  TaskListItemTypes,
  TreeItem,
} from 'shared/components/ui/virtualized-tree-list/types';
import { useToday } from 'shared/contexts/today';
import { useTaskDateShortcuts } from 'shared/hooks/use-task-date-shortcuts';
import { useTaskPriorityShortcuts } from 'shared/hooks/use-task-priority-shortcuts';
import { ID } from 'shared/types/id';
import { Task } from 'shared/types/task';

import { useTypeTree } from '../../hooks/use-type-tree';
import { GroupedListProps } from '../../types';
import * as Styled from './type-grouping-tasks-list.style';
import { TypeRenderData, TypeRenderDataContext } from './type-render-context';
import { TypeRenderItem } from './type-render-item';

const taskItemTypes = [TaskListItemTypes.Task];

export const TypeGroupingTasksList: React.FC<GroupedListProps> = ({
  tasks,
  sortingMode,
  onReorder,
  customOrder = {},
  collapsedSections = [],
  onSectionCollapse,
  weekStartsOn,
  scrollElement,
  denseFormat,
  groupSettings,
  isContextMenuOpen,
  defaultNewTaskDate,
}) => {
  const today = useToday();

  const tasksMap = useMemo(
    () =>
      tasks.reduce<Record<ID, Task>>((acc, task) => {
        acc[task.id] = task;
        return acc;
      }, {}),
    [tasks],
  );

  const treeData = useTypeTree({
    tasks,
    customOrder,
    sortingMode,
    today,
    weekStartsOn,
    showEmptyGroups: groupSettings.showEmptyGroups,
    hideNewTaskRow: groupSettings.hideNewTaskRow,
  });

  const _onReorder = (updatedItems: TreeItem[]) => {
    if (!onReorder) {
      return;
    }

    const result = onTypeReorder({ updatedItems });

    onReorder(result.updatedSections, result.updatedTasks);
  };

  const renderData = useMemo<TypeRenderData>(
    () => ({
      groupSettings,
      tasksMap,
      tasks,
      today,
      defaultNewTaskDate,
      denseFormat,
    }),
    [groupSettings, tasksMap, tasks, today, defaultNewTaskDate, denseFormat],
  );

  const [tree] = useTree();
  const { submit: updateTasks } = useUpdateTasks();

  useTaskPriorityShortcuts(tree, tasks, updateTasks);
  useTaskDateShortcuts(tree, tasks, updateTasks);

  return (
    <Styled.Container>
      <TypeRenderDataContext.Provider value={renderData}>
        <VirtualizedTreeList
          rootItemId={rootId}
          items={treeData}
          renderItem={TypeRenderItem}
          scrollElement={scrollElement}
          multiselectTypes={taskItemTypes}
          dragTypes={taskItemTypes}
          onReorder={_onReorder}
          collapsedItems={collapsedSections}
          onCollapse={onSectionCollapse}
          denseFormat={denseFormat}
          isContextMenuOpen={isContextMenuOpen}
        />
      </TypeRenderDataContext.Provider>
    </Styled.Container>
  );
};
